import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import useRetryFetch from "../../hooks/useRetryFetch";
import { queryClient } from "../../lib/queryClient";
import { trackCapture } from "../../lib/captureAnalytics";
import { commitCapture, type BodySuggestion as BodySuggestionData, type CaptureLocale } from "../../api/capture";
import { Button } from "../ui/button";

/**
 * "Several thoughts about X lately. Make it a project?" (spec 2026-11-02 §6).
 * The server already throttles this to once per 7 days; dismissing just hides it.
 */
const BodySuggestion = ({
  body,
  currentCaptureId,
  locale,
}: {
  body: BodySuggestionData;
  currentCaptureId: string;
  locale: CaptureLocale;
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [state, setState] = useState<"open" | "busy" | "done" | "dismissed">("open");

  useEffect(() => {
    trackCapture("capture_body_suggested");
  }, []);

  const accept = async () => {
    setState("busy");
    try {
      const linkCaptureIds = body.includesNew ? [...body.captureIds, currentCaptureId] : body.captureIds;
      await commitCapture(retryFetch, {
        text: body.theme,
        source: "body",
        locale,
        whole: { text: body.theme, kind: "project" },
        parts: [],
        linkCaptureIds,
      });
      trackCapture("capture_body_accepted");
      queryClient.invalidateQueries({ queryKey: ["cockpit-projects"] });
      toast.success(t("capture.body.created"));
      setState("done");
    } catch {
      toast.error(t("capture.saveError"));
      setState("open");
    }
  };

  if (state === "done" || state === "dismissed") return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
      <p className="text-sm text-start">{t("capture.body.prompt", { theme: body.theme })}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-full" disabled={state === "busy"} onClick={accept}>
          {state === "busy" && <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />}
          {t("capture.body.accept")}
        </Button>
        <Button size="sm" variant="ghost" disabled={state === "busy"} onClick={() => setState("dismissed")}>
          {t("capture.body.dismiss")}
        </Button>
      </div>
    </div>
  );
};

export default BodySuggestion;
